import { Box, Stack } from '@mui/material'
import { forwardRef, useEffect, useRef, useState } from 'react'

import { Color } from '../../chess/types'
import { GameViewState, StateChangeRequest } from '../../features/gameView/GameViewModel'
import ConfirmationDialogs, {
    ConfirmationType,
} from '../../features/gameView/components/confirmationDialogs/ConfirmationDialogs'
import MatchMakingDrawer from '../../features/matchMakingDrawer/MatchMakingDrawer'
import { analyticsManager } from '../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { Layout, useResponsiveSizings } from '../../sharedComponents/src/hooks/useResponsiveSizings'
import { useStoreActions, useStoreState } from '../../store/hooks'
import { MMChallengeType } from '../../store/types'
import { FTUE } from '../FTUE/FTUE'
import { ConnectionFailedOverlay } from '../overlays/ConnectionFailedOverlay/ConnectionFailedOverlay'
import { ResizableBoardWrapper } from './ResizableBoardWrapper'
import { OpponentDisconnectedSnackbar } from './StyledSnackbar/OpponentDisconnectedSnackbar'

type Props = {
    aboveBoard?: JSX.Element
    board: JSX.Element
    belowBoard?: JSX.Element
    children?: React.ReactNode
    dataProvider?: JSX.Element
    boardHidden?: boolean
    compactChild?: boolean
    background?: string
}

export const sizings = {
    desktopHeader: 80,
    mobileHeader: 52,
    margin: 32,
    rightPanelMin: 356,
    rightPanelMax: 450,
    gap: 16,
    minBoardSize: 300,
}

export const pixelSizings = Object.fromEntries(Object.entries(sizings).map(([key, value]) => [key, value + 'px']))

const opponentColor = {
    [Color.White]: 'b',
    [Color.Black]: 'w',
}

const AppLayout = forwardRef<HTMLDivElement, Props>(
    (
        {
            aboveBoard,
            board,
            belowBoard,
            dataProvider,
            children,
            boardHidden,
            compactChild,
            background = 'missions',
        }: Props,
        ref,
    ) => {
        const { layout } = useResponsiveSizings()
        const boardContainerRef = useRef<HTMLDivElement>(null)
        const childrenRef = useRef<HTMLDivElement>(null)
        const parentRef = useRef<HTMLDivElement>(null)
        const myColor = useStoreState((state) => state.gameView.myColor)
        const rematchStatus = useStoreState((state) => state.matchMaker.rematchStatus)
        const rematchMatchData = useStoreState((state) => state.matchMaker.rematchMatchData)
        const drawerOpen = useStoreState((state) => state.drawerOpen)
        const previousBackground = useStoreState((state) => state.previousBackground)
        const gameState = useStoreState((state) => state.gameView.gameState)
        const connectionStatus = useStoreState((state) => state.matchMaker.connectionStatus)
        const alreadyConnected = useStoreState((state) => state.matchMaker.alreadyConnected)

        const setGameEndEnabled = useStoreActions((state) => state.gameView.setGameEndEnabled)
        const acceptChallenge = useStoreActions((state) => state.matchMaker.acceptChallenge)
        const sendRejectChallenge = useStoreActions((state) => state.matchMaker.sendRejectChallenge)
        const setRematchStatus = useStoreActions((state) => state.matchMaker.setRematchStatus)
        const setPreviousBackground = useStoreActions((state) => state.setPreviousBackground)

        const [dialog, setDialog] = useState<ConfirmationType | undefined>()

        useEffect(() => {
            return () => {
                setPreviousBackground(background)
            }
        }, [])

        // disable scroll when touching the board on mobile
        useEffect(() => {
            const preventScroll = (event) => {
                event.preventDefault()
            }
            if (boardContainerRef.current) {
                boardContainerRef.current?.addEventListener('touchmove', preventScroll), { passive: 'false' }
                // DESKTOP
                // boardContainerRef.current?.addEventListener('wheel', preventScroll), { passive: 'false' }
            }
            return () => {
                if (boardContainerRef.current) {
                    boardContainerRef.current?.removeEventListener('touchmove', preventScroll)
                    // DESKTOP
                    // boardContainerRef.current?.removeEventListener('wheel', preventScroll)
                }
            }
        }, [boardContainerRef.current])

        // if a rematch offer has come in, open the dialog
        useEffect(() => {
            if (rematchStatus === StateChangeRequest.RECEIVED && gameState !== GameViewState.GAME_RUNNING)
                setDialog(ConfirmationType.ACCEPT_REMATCH)
        }, [rematchStatus])

        //  confirm dialog confirm handling
        const handleConfirm = (type: ConfirmationType) => {
            if (type === ConfirmationType.ACCEPT_REMATCH) {
                setRematchStatus(undefined)
                if (rematchMatchData) {
                    acceptChallenge({ id: rematchMatchData.pairId, challengeType: MMChallengeType.DIRECT })
                    setGameEndEnabled(false)
                    analyticsManager.dispatchEvent('offerRematchResult', {
                        result: 'accepted',
                        parentMatchID: rematchMatchData.pairId,
                    })
                    // GTM also expects separate acceptRematch event
                    analyticsManager.dispatchEvent('acceptRematch', {
                        parentMatchID: rematchMatchData.pairId,
                    })
                }
            }
            setDialog(undefined)
        }

        //  confirm dialog cancel handling
        const handleCancel = (type: ConfirmationType) => {
            if (type === ConfirmationType.ACCEPT_REMATCH) {
                setRematchStatus(undefined)
                if (rematchMatchData) {
                    sendRejectChallenge({ pairId: rematchMatchData.pairId, isRematch: true })
                    analyticsManager.dispatchEvent('offerRematchResult', {
                        result: 'rejected',
                        parentMatchID: rematchMatchData.pairId,
                    })
                }
            }
            setDialog(undefined)
        }

        const isPlayZone = window.location.pathname === '/'

        const hideBoard = layout === Layout.MOBILE && drawerOpen !== 'closed'

        return (
            <>
                {dataProvider}
                <Box
                    sx={{
                        '@media(min-width: 1024px)': {
                            '::after': {
                                content: '""',
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                bottom: 0,
                                right: 0,
                                width: '100%',
                                height: '100vh',
                                background: `url(/assets/backgrounds/${background}.svg)`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                zIndex: -1,
                                opacity: previousBackground === background ? 1 : 0,
                                transform: previousBackground === background ? 'scale(1)' : 'scale(1.5)',
                                transformOrigin: 'center',
                                animation:
                                    previousBackground === background
                                        ? 'none'
                                        : 'fadeInBackground 800ms ease-in-out forwards',
                            },
                        },
                        '@keyframes fadeInBackground': {
                            '0%': {
                                opacity: 0,
                                transform: 'scale(1.5)',
                            },
                            '100%': {
                                opacity: 1,
                                transform: 'scale(1)',
                            },
                        },
                    }}
                    ref={ref}
                >
                    <Stack
                        ref={parentRef}
                        direction={layout === Layout.MOBILE ? 'column' : 'row'}
                        alignItems="stretch"
                        justifyContent="center"
                        mx={{ xs: `calc(${pixelSizings.margin}/4)`, md: pixelSizings.margin }}
                        mb={{ xs: `calc(${pixelSizings.margin}/4)`, md: `calc(${pixelSizings.margin}/2)` }}
                        mt={{ xs: 1, md: `calc(${pixelSizings.margin}/2)` }}
                        gap={pixelSizings.gap}
                        height={{ xs: '', md: `calc(100vh - ${pixelSizings.margin} - ${pixelSizings.desktopHeader})` }}
                        margin="0 auto"
                    >
                        {!boardHidden && (!hideBoard || gameState === GameViewState.GAME_PRELOADING) ? (
                            <ResizableBoardWrapper childrenRef={childrenRef} parentRef={parentRef}>
                                <Box
                                    ref={childrenRef}
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    gap={{ xs: '12px', md: pixelSizings.gap }}
                                >
                                    {aboveBoard}
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        position="relative"
                                        ref={boardContainerRef}
                                        width="100%"
                                        sx={(theme) => ({
                                            display: 'flex',
                                            userSelect: 'none',
                                            // overflow: 'hidden', // fix for DEV-4840
                                            p: '5px',
                                            border: `2px solid ${theme.palette.primary.light} !important`,

                                            // ENEMY CHESS PIECES ALWAYS VISIBLE
                                            [`div[color="figure-${opponentColor[myColor]}"]`]: {
                                                opacity: isPlayZone ? '1 !important' : 'unset',
                                            },
                                            '> div': {
                                                borderRadius: '7px',
                                                overflow: 'hidden',
                                            },
                                        })}
                                    >
                                        {board}
                                    </Box>
                                    {belowBoard}
                                </Box>
                            </ResizableBoardWrapper>
                        ) : null}
                        <Box
                            flexGrow={1}
                            width="100%"
                            // MIGHT BREAK STUFF
                            overflow="visible"
                            minWidth={{ xs: '100%', md: pixelSizings.rightPanelMin }}
                            maxWidth={{ xs: '100%', md: pixelSizings.rightPanelMax }}
                            maxHeight={{ xs: 'unset', md: '1080px' }}
                        >
                            <Box
                                display="flex"
                                alignItems="stretch"
                                flexDirection="column"
                                flexGrow={1}
                                justifyContent="flex-start"
                                height={{
                                    xs:
                                        hideBoard || boardHidden
                                            ? `calc(100dvh - ${pixelSizings.margin}/2  - ${pixelSizings.mobileHeader})`
                                            : compactChild
                                              ? '100%'
                                              : 'max(700px, 80dvh)',
                                    md: '100%',
                                }}
                                gap="12px"
                            >
                                {gameState !== GameViewState.GAME_PRELOADING && <MatchMakingDrawer />}
                                {!hideBoard || gameState === GameViewState.GAME_PRELOADING ? children : null}
                            </Box>
                        </Box>
                    </Stack>
                </Box>
                {dialog && (
                    <ConfirmationDialogs
                        type={dialog}
                        onClose={handleCancel}
                        onConfirm={handleConfirm}
                        rematchUser={rematchMatchData?.userName ? rematchMatchData.userName : 'Opponent'}
                    />
                )}
                <OpponentDisconnectedSnackbar />
                <ConnectionFailedOverlay connectionStatus={connectionStatus} alreadyConnected={alreadyConnected} />
                <FTUE />
            </>
        )
    },
)

export default AppLayout
